import React from "react";
import styled from "styled-components";
import { CalculateHeader as Header, Text } from "components";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import Actions from "store/plan/actions";
import { Popconfirm } from "antd";
import FilterSortButton from "components/molecules/FilterSortButton";

const Wrapper = styled.div`
  display: flex;
  margin: 10px 0;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 82%;
  transition: left 0.4s ease-in-out;
  position: relative;
  left: 0;
  margin-left: auto;
  margin-right: auto;
`;

const TestDiv = styled.div`
  margin: 25px 0;
  border: none;
  border-spacing: 0;
  align-items: center;
  overflow-x: auto;
  max-width: 100%;

  @media (max-width: 620px) {
    margin: 20px 10px;
  }
`;

const InnerWrapper = styled.table`
  margin: auto;
`;

const Row = styled.tr`
  ${(props) =>
    props.sticky &&
    `
    position: sticky;
    ${props.stickyPosition}: ${props.stickyOffset}px;
    background: #fff;
    z-index: 1;
  `}
`;

const TableBody = styled.tbody`
  /* display: inline-block;
  height: 300px;
  overflow-y: scroll; */
`;

const CellDiv = styled.div`
  text-align: ${(props) => (props.align ? props.align : "center")};
  padding: 2px;
  width: ${(props) => (props.width ? props.width : "100px")};
  max-width: ${(props) => (props.width ? props.width : "")};
  ${(props) => props.border && `border-right: 1px solid white`};
  ${(props) => props.border && `border-bottom: 1px solid white`};
  min-height: 50px;
  height: ${(props) => (props.tall ? "80px" : props.short ? "35px" : props.medium ? "65px" : "100%")};
  display: ${(props) => (props.hide ? "none" : "flex !important")};
  flex-direction: ${(props) => (props.col ? "column" : "row")};
  gap: ${(props) => (props.col ? "2px" : "0px")};
  align-items: center !important;
  ${(props) =>
    props.justifyContent
      ? `justify-content: ${props.justifyContent} !important`
      : `justify-content: center !important`};

  transition: all 0.3s ease;

  ${({ background, opacity }) =>
    background === "green"
      ? `background: rgba(147, 193, 159, ${opacity ?? 1})`
      : background === "green-2"
      ? `background: rgba(167, 204, 177, ${opacity ?? 1})`
      : background === "green-3"
      ? `background: rgba(188, 215, 195, ${opacity ?? 1})`
      : background === "green-4"
      ? `background: rgba(208, 227, 213, ${opacity ?? 1})`
      : background === "green-5"
      ? `background: rgba(229, 238, 231, ${opacity ?? 1})`
      : background === "red"
      ? `background: rgba(246,180,183, ${opacity ?? 1})`
      : background === "grey"
      ? "background: rgba(249, 249, 249)"
      : background === "catastrophic"
      ? "background: rgba(177, 30, 26)"
      : background === "bronze"
      ? "background: rgba(188, 106, 46)"
      : background === "silver"
      ? // ? "background: rgba(216, 215, 215)"
        "background: #F2F4F7"
      : background === "gold"
      ? "background: rgba(248, 191, 80)"
      : background === "platinum"
      ? "background: rgba(192,192,192)"
      : background === "recommended"
      ? "background: rgba(28,53,94)"
      : "background: rgba(255,255,255,0)"};

      :hover {
        ${(props) =>
          props.canEdit &&
          `
        cursor: pointer;
      `}
`;

const TableCell = styled.td`
  border: none;
  background-color: #fff;
  padding: 1px 1px;
  height: 0px;

  ${(props) =>
    props.sticky &&
    `
    position: sticky;
    left: 0;
  `}
`;

const StyledCell = (props) => (
  <TableCell sticky={props.sticky}>
    <CellDiv {...props}>{props.children}</CellDiv>
  </TableCell>
);

const PrescriptionDrugsTable = ({
  report,
  birthParentPlans,
  partnerPlans,
  pbms,
  toggleSideBar,
  toggleAssumptions,
  isAdmin,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const handleAddDrugsForm = () => {
    dispatch(Actions.Creators.changeCurrent(0));
    localStorage.removeItem("drugsForm");
    history.push(`/add-drugs`, {
      reportId: report._id,
      returnToDetailsPage: true,
      activeTab: 2,
    });
  };

  const { expected, unexpected } = report;
  const drugs = [...new Map([...expected.drugs, ...unexpected.drugs].map((drug) => [drug.name, drug])).values()];
  const plans = [...birthParentPlans, ...partnerPlans];

  const tiersDisplayName = {
    not_covered: "Not Covered",
    preventive_care: "Preventive",
    unknown: "Unknown",
  };

  const getBackgroundColor = (tierList, tier) => {
    switch (tier) {
      case "unknown":
        return "red";
      case "not_covered":
        return "red";
      case "preventive_care":
        return "green";
      default:
        return tierList.indexOf(tier) === 4 ? "grey" : `green-${tierList.indexOf(tier) + 2}`;
    }
  };

  const lastRow = drugs.reduce((acc, _drug) => {
    pbms.forEach((_pbm, i) => {
      acc[i] += _drug.tiers[_pbm._id] !== "not_covered" ? 1 : 0;
    });
    return acc;
  }, new Array(pbms.length).fill(0));

  return (
    <Wrapper>
      <div style={{ width: "100%" }}>
        <Header title="Prescription Drugs" bigTitle />
      </div>
      <FilterSortButton toggleSideBar={toggleSideBar} toggleAssumptions={toggleAssumptions} />
      <TestDiv>
        <InnerWrapper>
          <Row
            style={{ flexFlow: "row" }}
            align="middle"
            justify="center"
            wrap={false}
            sticky
            stickyPosition="bottom"
            stickyOffset="50"
          >
            <StyledCell sticky />
          </Row>
          <Row
            style={{ flexFlow: "row" }}
            align="middle"
            justify="center"
            wrap={false}
            sticky
            stickyPosition="top"
            stickyOffset="0"
          >
            <StyledCell sticky />
            {pbms.map((pbm, i) => (
              <Popconfirm
                key={`${i}`}
                title="Would you like to edit these PBMs and Drugs?"
                disabled={!isAdmin}
                onConfirm={handleAddDrugsForm}
                okText="Yes"
                cancelText="No"
              >
                <StyledCell
                  tall
                  border
                  background={"silver"}
                  borderTopLeft={i === 0}
                  borderTopRight={i === pbm.length - 1}
                >
                  <Text bold fontSize="16">
                    {pbm.name}
                  </Text>
                </StyledCell>
              </Popconfirm>
            ))}
          </Row>
          <TableBody>
            {drugs.map((drug, i) => {
              const drugsTiersSplit = Object.fromEntries(
                Object.keys(drug.tiers).map((tier) => [
                  tier,
                  {
                    code: drug.tiers[tier].split(" - ")[0],
                    name: drug.tiers[tier].split(" - ")[1],
                  },
                ]),
              );

              return (
                <Row
                  key={`${drug.name}-${i}`}
                  style={{ flexFlow: "row", height: "fit-content" }}
                  align="middle"
                  justify="center"
                  wrap={false}
                >
                  <StyledCell sticky justifyContent="end" align="right" width="100%" maxWidth="150px">
                    <Text fontSize={14} align="left">
                      {drug.name}
                    </Text>
                  </StyledCell>
                  {pbms.map((pbm, j) => {
                    return (
                      <Popconfirm
                        key={`${i}-${j}`}
                        title="Would you like to edit these PBMs and Drugs?"
                        disabled={!isAdmin}
                        onConfirm={handleAddDrugsForm}
                        okText="Yes"
                        cancelText="No"
                      >
                        <StyledCell
                          border
                          background={getBackgroundColor(
                            pbm.tiers.map((tier) => `${tier.code} - ${tier.name}`),
                            drug.tiers[pbm._id],
                          )}
                          opacity={0.5}
                          col={true}
                          borderBottomLeft={j === 0 && i === drugs.length - 1}
                          borderBottomRight={j === plans.length - 1 && i === drugs.length - 1}
                        >
                          <Text fontSize={14}>
                            {tiersDisplayName[drug.tiers[pbm._id]] || drugsTiersSplit[pbm._id].name}
                          </Text>
                          {!tiersDisplayName[drug.tiers[pbm._id]] && (
                            <Text fontSize={10}>{`(Tier ${drugsTiersSplit[pbm._id].code})`}</Text>
                          )}
                        </StyledCell>
                      </Popconfirm>
                    );
                  })}
                </Row>
              );
            })}
            <Row
              style={{ flexFlow: "row" }}
              align="middle"
              justify="center"
              wrap={false}
              sticky
              stickyPosition="bottom"
              stickyOffset="50"
            >
              <StyledCell sticky />
            </Row>
            <Row
              style={{ flexFlow: "row" }}
              align="middle"
              justify="center"
              wrap={false}
              sticky
              stickyPosition="bottom"
              stickyOffset="0"
            >
              <StyledCell sticky justifyContent="end" align="right" width="100%" maxWidth="150px">
                <Text fontSize={14} bold align="left">
                  {"Total"}
                </Text>
              </StyledCell>
              {lastRow.map((element, i) => (
                <StyledCell
                  key={`totals[${i}]`}
                  border
                  background={element === drugs.length ? "green" : "red"}
                  opacity={0.5}
                  borderBottomLeft={i === 0}
                  borderTopLeft={i === 0}
                  borderTopRight={i === lastRow.length - 1}
                  borderBottomRight={i === lastRow.length - 1}
                >
                  <Text fontSize={14}>{element}</Text>
                </StyledCell>
              ))}
            </Row>
          </TableBody>
        </InnerWrapper>
      </TestDiv>
    </Wrapper>
  );
};

export default PrescriptionDrugsTable;
